/**
 * RentalQuotationSection — "จากใบเสนอราคา" ในหน้ารายละเอียดการเช่า (มติ PO 29 ก.ย. 2026 เฟส 4)
 *
 * สัญญาที่เกิดจากใบเสนอราคา (ผ่านใบงานเตรียมเครื่อง) ย้อนกลับไปดูข้อเสนอเดิมได้จากที่นี่ —
 * บัญชีเห็น "มัดจำที่เสนอ" และเงื่อนไขที่เซลตกลงกับลูกค้าโดยไม่ต้องไปค้นหน้ารายการ
 *
 * backend: `GET /quotations/?rental_id=...` (ใบที่ผูกสัญญานี้ตรงหรือผ่านใบงาน) · perm `quotations.view`
 * ไม่มีสิทธิ์ = **ไม่ยิง API และไม่แสดง** (pattern G-27 กัน 403 เงียบ ๆ) · ไม่มีใบ = ไม่มีการ์ดเปล่า
 */
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { RiFileTextLine, RiErrorWarningLine } from 'react-icons/ri';
import quotationService from '../../services/quotationService';
import QuotationStatusBadge from '../billing/QuotationStatusBadge';
import { usePermission } from '../../hooks/usePermission';
import { formatDate } from '../../utils/dateUtils';

interface RentalQuotationSectionProps {
  rentalId: number;
}

const formatMoney = (value: number | string | undefined | null): string => {
  const n = typeof value === 'string' ? parseFloat(value) : value ?? 0;
  return (Number.isFinite(n) ? n : 0).toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const RentalQuotationSection: React.FC<RentalQuotationSectionProps> = ({ rentalId }) => {
  const { hasPermission } = usePermission();
  const canView = hasPermission('quotations.view');

  const { data, isLoading, error } = useQuery({
    queryKey: ['quotations', 'by-rental', rentalId],
    queryFn: () => quotationService.getQuotations({ rental_id: rentalId, limit: 5 }),
    // ไม่มีสิทธิ์ดู = ไม่ยิง API
    enabled: canView && !!rentalId,
    retry: false,
    staleTime: 60 * 1000,
  });

  if (!canView) return null;

  const quotations = data?.items || [];
  if (!isLoading && !error && quotations.length === 0) return null;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-secondary-200">
      <div className="px-6 py-4 border-b border-secondary-200 flex items-center justify-between gap-2">
        <h2 className="text-lg font-medium text-secondary-900 flex items-center">
          <RiFileTextLine className="w-5 h-5 mr-2 text-secondary-500" />
          จากใบเสนอราคา
          {quotations.length > 1 && (
            <span className="ml-2 bg-secondary-100 text-secondary-600 text-sm px-2 py-0.5 rounded-full">
              {quotations.length}
            </span>
          )}
        </h2>
      </div>

      <div className="p-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-6 text-secondary-500">
            <span className="text-sm">กำลังโหลด...</span>
          </div>
        ) : error ? (
          <div className="flex items-start gap-2 text-sm text-secondary-500">
            <RiErrorWarningLine className="w-4 h-4 text-warning-500 flex-shrink-0 mt-0.5" />
            <span>
              ไม่สามารถโหลดใบเสนอราคาของสัญญานี้ได้ — ดูรายการทั้งหมดได้ที่{' '}
              <Link to="/billing/quotations" className="text-primary-600 hover:underline">
                ใบเสนอราคา
              </Link>
            </span>
          </div>
        ) : (
          <div className="space-y-3">
            {quotations.map((q) => {
              const deposit = Number(q.deposit_offered_total ?? 0);
              return (
                <Link
                  key={q.id}
                  to={`/billing/quotations/${q.id}`}
                  className="block border border-secondary-200 rounded-lg p-4 hover:bg-secondary-50 transition-colors"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                    <span className="font-medium text-secondary-900">{q.quotation_number}</span>
                    <QuotationStatusBadge status={q.status} />
                  </div>
                  <p className="text-sm text-secondary-700">
                    {q.job_name ? `${q.job_name} · ` : ''}
                    ยอดสุทธิ {formatMoney(q.net_total)} บาท
                    {q.salesperson_name ? ` · ผู้เสนอราคา ${q.salesperson_name}` : ''}
                  </p>
                  <p className="text-xs text-secondary-500">
                    วันที่ {formatDate(q.quotation_date)} · มีผลถึง {formatDate(q.valid_until)}
                    {deposit > 0 ? ` · มัดจำที่เสนอ ${formatMoney(deposit)} บาท (บัญชีกรอกที่สัญญา)` : ''}
                  </p>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default RentalQuotationSection;
